import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { bindActionCreators } from 'redux';
import { Button, Typography } from '@material-ui/core';
import { Redirect } from 'react-router';
import { PrevIcon, NextIcon } from '../../common/icons';

import * as PublicStore from '../../app/store/PublicStore';
import urls from '../../app/urls';
import { InfoMessage } from '../../common/components';
import PublicTaskContent from './components/PublicTaskContent';
import { PUBLIC_PROGRAM_RECEIVED } from '../../app/store/actions';
import { DelayedFetch } from '../../youwell-common/components';

const styles = theme => ({
    content: {
        border: theme.borders.dim,
    },
    taskTitle: {
        marginBottom: theme.spacing(4),
    },
    actions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: `${theme.spacing(4)}px 0`,
    },
    singleactions: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: `${theme.spacing(4)}px 0`,
    },
    navIconLeft: {
        marginRight: theme.spacing(1),
    },
    navIconRight: {
        marginLeft: theme.spacing(1),
    },
});

class Task extends React.Component {
    taskContentRef = React.createRef()

    clickNext = nextDirection => () => {
        const { program, match } = this.props;
        const { programId, moduleIndex, taskIndex } = match.params;

        const module = program && program.modules && program.modules[moduleIndex];
        const task = module && module.tasks && module.tasks[taskIndex];

        const currentStep = Number(taskIndex);
        const nextTaskNr = currentStep + nextDirection;
        const isLastTask = nextTaskNr > module.tasks.length - 1;

        let redirect = '';
        if (isLastTask || nextTaskNr < 0) redirect = `${urls.public.home}/${programId}`;
        else redirect = `${urls.public.program}/${programId}/${moduleIndex}/${nextTaskNr}`;

        const go = () => {
            this.props.completeTask({ taskId: task.id, stepNr: currentStep }, redirect);
        };

        if (nextDirection > 0) {
            this.taskContentRef.current?.validateAndSave(go);
        } else {
            this.taskContentRef.current?.saveWithoutValidation(go);
        }
    }

    visitTask = () => {
        const { program, match, patientProgramElementId } = this.props;
        const { moduleIndex, taskIndex } = match.params;

        const module = program && program.modules && program.modules[moduleIndex];
        const task = module && module.tasks && module.tasks[taskIndex];

        if (patientProgramElementId && task) {
            this.props.visitTask({
                ppeId: patientProgramElementId,
                id: task.id,
            });
        }
    }

    endSession = () => {
        this.props.endSession(PublicStore.endSessionReasons.completed);
    }

    render() {
        const {
            classes, translate, program, match, hasLoaded, latestCompletedStep, clientConfig,
        } = this.props;
        const { moduleIndex, taskIndex } = match.params;

        const module = program && program.modules && program.modules[moduleIndex];
        const task = module && module.tasks && module.tasks[taskIndex];

        if (!task) {
            return hasLoaded ? (<InfoMessage messageKey="noContent" />) : null;
        }

        const taskIndexNr = Number(taskIndex || 0);

        const isFirstTask = !taskIndex || taskIndex === '0';
        const moreElementsInGroup = module.tasks.length > 1;
        const isLastTask = !moreElementsInGroup || taskIndexNr === module.tasks.length - 1;

        if (taskIndexNr > (latestCompletedStep + 1) && !isLastTask) {
            return (
                <Redirect
                    to={`${urls.public.program}/${program.id}/${moduleIndex}/${Math.max(0, latestCompletedStep)}`}
                />
            );
        }

        return (
            <div className={classes.root}>
                <Typography variant="h4" className={classes.taskTitle}>
                    {task.name}
                </Typography>

                <DelayedFetch dataCall={this.visitTask} delaySeconds={clientConfig.appSetup.visitTaskDelay} />
                {isLastTask && <DelayedFetch dataCall={this.endSession} delaySeconds={clientConfig.appSetup.visitTaskDelay} />}

                <PublicTaskContent
                    program={program}
                    moduleId={module.id}
                    task={task}
                    ref={this.taskContentRef}
                />

                {!isLastTask && (
                    <div className={classes.actions}>
                        {!isFirstTask && (
                            <Button
                                onClick={this.clickNext(-1)}
                            >
                                <PrevIcon className={classes.navIconLeft} color="inherit" />
                                {translate('previous')}
                            </Button>
                        )}
                        <div />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={this.clickNext(1)}
                        >
                            {translate('next')}
                            <NextIcon className={classes.navIconRight} color="inherit" />
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

Task.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    clientConfig: PropTypes.object.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            programId: PropTypes.string.isRequired,
            moduleIndex: PropTypes.string.isRequired,
            taskIndex: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    completeTask: PropTypes.func.isRequired,
    visitTask: PropTypes.func.isRequired,
    hasLoaded: PropTypes.bool.isRequired,
    latestCompletedStep: PropTypes.number.isRequired,
    program: PropTypes.object,
    patientProgramElementId: PropTypes.string,
    endSession: PropTypes.func.isRequired,
};

Task.defaultProps = {
    program: null,
    patientProgramElementId: null,
};

const mapStateToProps = (state, ownProps) => ({
    translate: getTranslate(state.localize),
    clientConfig: state.auth.clientConfig,
    program: state.publicStore.details && state.publicStore.details[ownProps.match.params.programId],
    patientProgramElementId: state.publicStore.patientProgramElementId,
    hasLoaded: Boolean(state.publicStore.requests[`${PUBLIC_PROGRAM_RECEIVED}/${ownProps.match.params.programId}`]
    && state.publicStore.requests[`${PUBLIC_PROGRAM_RECEIVED}/${ownProps.match.params.programId}`].completed),
    latestCompletedStep: state.publicStore.latestCompletedStep,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PublicStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Task));
